import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { ESCAPE_LAYER, useEscapeLayer } from "./useEscapeLayer";

/**
 * WHAT A MODAL OWES A KEYBOARD — one implementation, three surfaces.
 *
 * This is `components/ui/Dialog.tsx`'s contract, lifted out of it verbatim so
 * that surfaces which are modal but are NOT centred boxes can hold it too:
 * the mobile navigation drawer and the emoji bottom sheet (OSO-638). Both were
 * overlays that covered the page and left focus behind them, which is the
 * definition of not being a dialog — typing went to whatever was focused
 * before, Tab walked out into the content the backdrop was covering, and a
 * screen reader was told nothing had happened.
 *
 * The chrome stays with each caller, because that is the part that actually
 * differs — centred panel, left drawer, bottom sheet. Only the behaviour is
 * shared:
 *
 *   1. Focus moves in when the surface opens — unless something inside has
 *      already claimed it. A child that autofocuses (the composer's write
 *      field) runs its effect before this one, and taking focus back off it to
 *      hand it to the close button would be the bug this exists to fix.
 *   2. Tab is trapped inside the panel while it is open.
 *   3. Escape closes, unless the caller already answers Escape itself.
 *   4. Focus is restored to whatever had it when the surface opened.
 *
 * WHY IT IS NOT JUST `<Dialog>` WITH MORE VARIANTS. A drawer is not a dialog
 * with different padding: it is full-height and edge-anchored, the sheet is
 * bottom-anchored and drag-shaped, and encoding those as `align` values would
 * put three unrelated layouts behind one `cn()` call. The behaviour is what
 * they genuinely share.
 */
export interface ModalSurfaceOptions {
  open: boolean;
  onClose?: () => void;
  /**
   * Where this surface sits in the Escape stack — see `ESCAPE_LAYER`. Defaults
   * to `dialog`, which is right for anything painted at `z-50`.
   */
  layer?: number;
  /**
   * Set `false` when the CALLER already consumes Escape and this hook must not
   * answer it a second time. `EmojiPicker` does: its `useDismissable` has
   * closed the picker on Escape since OSO-402, and it marks the key spent so
   * one keystroke cannot also reach the composer dialog behind it. Focus-in,
   * the trap and the restore all still apply.
   */
  escape?: boolean;
}

export function useModalSurface<T extends HTMLElement>({
  open,
  onClose,
  layer = ESCAPE_LAYER.dialog,
  escape = true,
}: ModalSurfaceOptions): RefObject<T | null> {
  const panelRef = useRef<T>(null);
  const isTopmost = useEscapeLayer(open, layer, panelRef);
  useFocusContainment({ open, panelRef, isTopmost, onClose, escape });
  return panelRef;
}

/**
 * THE FOCUS HALF ON ITS OWN, for a surface that already owns its place in the
 * Escape stack and cannot register a second time.
 *
 * `ImageLightbox` is the one (OSO-639). It registers at `ESCAPE_LAYER.lightbox`
 * and reads the resulting `isTopmost` from FOUR different guards — its Escape
 * branch, and three more on the arrow keys that distinguish "is anything drawn
 * over me" from "is another lightbox drawn over me" (a hover card at `z-[200]`
 * must not kill image navigation; a second lightbox opened from a comment in
 * the sidebar must). None of that is focus management, all of it is load-
 * bearing, and `useModalSurface` would have registered the panel a second time
 * underneath it.
 *
 * So the lightbox keeps its keyboard logic and takes only this: focus in, Tab
 * trapped, focus restored.
 */
export function useFocusContainment<T extends HTMLElement>({
  open,
  panelRef,
  isTopmost,
  onClose,
  escape = false,
}: {
  open: boolean;
  panelRef: RefObject<T | null>;
  isTopmost: (scope?: "all" | "rank") => boolean;
  onClose?: () => void;
  escape?: boolean;
}): void {

  // Read from a ref, as `useDismissable` does and for the same reason: callers
  // pass an inline closure, and re-running the effect below on every render
  // would re-run its cleanup — which restores focus, and would therefore drag
  // the caret back to wherever it was when the surface opened, mid-typing.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  /**
   * Who gets focus back, and why this is a layout effect. A child's own
   * autofocus is a passive effect, and every layout effect runs before every
   * passive one — so this still sees the trigger, where the effect below
   * (passive, because it must run after the panel is on screen) would already
   * see the write field the composer moved focus to. Anything that took focus
   * earlier is left unrecorded rather than restored to itself. Cleared on
   * close, so a surface opened twice captures the trigger of each opening.
   */
  const restoreTo = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement as HTMLElement | null;
    if (!panelRef.current?.contains(active)) restoreTo.current = active;
  }, [open, panelRef]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    if (!panel.contains(document.activeElement)) {
      (focusablesIn(panel)[0] ?? panel).focus();
    }

    function handleKeyDown(e: KeyboardEvent) {
      const el = panelRef.current;
      if (!el) return;
      /**
       * A key something inside has already answered is not ours.
       * `RichTextInput` calls `preventDefault()` on the Escape that dismisses
       * its @-mention or :emoji suggestions; without this, that Escape would
       * keep travelling and close the composer the author was typing in — one
       * keystroke dismissing a dropdown *and* asking to discard a half-written
       * post. Tab is the same story where a nested widget consumes it to
       * accept a suggestion.
       */
      if (e.defaultPrevented) return;
      if (e.key === "Escape") {
        if (!escape) return;
        if (!isTopmost()) return;
        // Whoever answers marks the key spent — the invariant every other
        // registered layer holds, and what stops a listener this one has no
        // ordering relationship with (a future capture-phase consumer, or
        // anything registered later) acting on the same keystroke.
        e.preventDefault();
        closeRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;
      // Only another MODAL surface stands this trap down. Escape asks "is
      // anything drawn over me"; Tab asks "is another *modal* thing over me",
      // and the answers differ because most of what can be drawn over one is
      // not modal and traps nothing — the hover card is `z-[200]`, so resting
      // the pointer on an author name inside an open panel makes it topmost,
      // and a trap that stood down for it would let the next Tab walk out.
      if (!isTopmost("rank")) return;
      const stops = focusablesIn(el);
      if (stops.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      // Focus that has escaped the panel (or sits on the panel itself) is
      // pulled back to whichever end Tab was heading for.
      if (e.shiftKey && (active === first || !el.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !el.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Only if the surface still holds the caret. Something that moved focus
      // on its way out — a confirmation opening on top, a navigation — has
      // already decided where it belongs, and yanking it back to the trigger
      // would undo that.
      const active = document.activeElement;
      if (active === document.body || panel.contains(active)) {
        restoreTo.current?.focus?.();
      }
      restoreTo.current = null;
    };
  }, [open, isTopmost, escape, panelRef]);
}

/**
 * Everything Tab can reach — the trap's own definition of "inside". The panel
 * itself carries `tabIndex={-1}` and is deliberately excluded: it is the focus
 * of last resort, not a stop on the way round.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

/**
 * MATCHING THE SELECTOR IS NOT BEING REACHABLE, and the difference is a hard
 * keyboard trap rather than a cosmetic one.
 *
 * `ImageLightbox` is the first caller with RESPONSIVE DUPLICATES of its own
 * chrome: a close button inside `lg:hidden` and a thumbnail strip inside
 * `hidden lg:block`. One of the two is always `display: none`, and `.focus()`
 * on a `display: none` element is a no-op by spec — so on desktop the FIRST
 * match in DOM order is the mobile close button, focus-in silently does
 * nothing, and focus stays on the trigger behind the backdrop. The trap then
 * makes it worse: `active` is outside the panel, so every forward Tab runs
 * `preventDefault(); first.focus()` — another no-op — and Tab is dead in both
 * senses. Mirror image on a phone, where the last match is a desktop thumbnail
 * and focus escapes the modal instead.
 *
 * `checkVisibility` IS THE TEST, WHERE THERE IS ONE. It is the only API that
 * answers the actual question (is this rendered, per CSS) rather than a proxy;
 * `offsetParent` and `getClientRects()` are the usual stand-ins and BOTH
 * report "invisible" for everything under jsdom, which has no layout — a
 * filter built on them would empty this list in every test in the suite while
 * looking correct. Where the method is missing (jsdom, and browsers older than
 * Chrome 105 / Firefox 106 / Safari 17.4) every match is kept, which is
 * exactly the behaviour that shipped before this filter existed.
 */
type MaybeCheckable = HTMLElement & {
  checkVisibility?: (options?: { checkVisibilityCSS?: boolean }) => boolean;
};

export function focusablesIn(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    const check = (el as MaybeCheckable).checkVisibility;
    return check ? check.call(el, { checkVisibilityCSS: true }) : true;
  });
}
